import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import "./user-account.css";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

import api from "../../../api/api";
import HintModal from "../common/hint-modal";
import filterUsersByName from "../common/filter-users-by-name";
// import TabsClient from "../../../Components/new-client-dashboard/Tabs";
import UserChatBubble from "../../Forms/FirmUserBtn";
import avatarImage from "../../../assets/images/avatar.svg";
import ChatSendingPopup from "../../Forms/common/ChatSendingPopup";
import UserPermissions from "../user-permissions/user-permissions";
import UserAvatar from "../user-avatar/user-avatar";
import addIconGold from "../../../assets/Add-icon-gold.svg";
import SmartResponsiveTabsContainer from "../../DefendantPage/DefandantMain/components/ResponsiveTabFirmSettings";
import usePermission from "../../../auth/usePermission";

import useUserAccounts from "./hooks/useUserAccount";
import ResetPasswordModal from "./Modals/reset-password-modal";
import useCheckAttorneyUser from "./hooks/useCheckAttorneyUser";
import EditUserModal from "./Modals/edit-user-modal";
import AddUserModalFirmSettings from "./add-user-modal";

const COLLAPSE_BREAKPOINT = 1230;

const useIsCollapsed = () => {
  const [isCollapsed, setIsCollapsed] = useState(
    () => window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT}px)`).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT}px)`);
    const handler = (e) => setIsCollapsed(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return isCollapsed;
};

const UserAccounts = () => {
  // const heading = "Enable high-level permissions for each user account";
  const points = [
    "Accounting: User can access the accounting functions.",
    "Admin: User can access administrative functions in firm settings.",
    "Intake: User can add new cases to the firm.",
    "Active: User account is active or not.",
    "Reset Password: The firm owner sets a new password for a user in this firm.",
  ];
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const canModifyFirmAvatar = usePermission("modify_firm_avatar");
  const canManagePermissions = usePermission("accounting_admin_permission");

  const isCollapsed = useIsCollapsed();

  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { data, _loading, _error, refetch } = useUserAccounts(activeTab);
  const { data: userExists, loading: _checkUser, error: _checkUserError } = useCheckAttorneyUser();
  const canResetPassword = userExists?.attorney_user === true;
  const isStatusTab = activeTab === "active" || activeTab === "in-active";
  const canEditUsers = isStatusTab ? canManagePermissions : Boolean(userExists?.attorney_user);
  const canEditRows = canEditUsers && isStatusTab;

  const [permissions, setPermissions] = useState([]);
  const filteredUsers = useMemo(
    () => filterUsersByName(permissions, searchTerm),
    [permissions, searchTerm]
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showHints, setShowHints] = useState(false);

  const [chatData, _setChatData] = useState(null);
  const [chatModal, setChatModal] = useState(false);

  const handleTabChange = useCallback(
    (id) => {
      if (id === "avatar" && !canModifyFirmAvatar) return;
      if (id === "permission" && !canManagePermissions) return;
      setActiveTab(id);
    },
    [canManagePermissions, canModifyFirmAvatar]
  );

  useEffect(() => {
    if (
      (activeTab === "avatar" && !canModifyFirmAvatar) ||
      (activeTab === "permission" && !canManagePermissions)
    ) {
      setActiveTab("active");
    }
  }, [activeTab, canManagePermissions, canModifyFirmAvatar]);

  const mainTabs = useMemo(
    () =>
      [
        {
          id: "active",
          label: "Active",
          className: "tab-trapezium-cycles-corners",
          background: "var(--primary-60)",
          activeColor: "white",
          onClick: () => handleTabChange("active"),
          leftHand: false,
        },
        {
          id: "in-active",
          label: "InActive",
          className: "tab-trapezium-cycles-corners",
          background: "var(--primary-60)",
          activeColor: "white",
          onClick: () => handleTabChange("in-active"),
          leftHand: false,
        },
        {
          id: "all_",
          label: "All",
          className: "tab-trapezium-cycles-corners",
          background: "var(--primary-60)",
          activeColor: "white",
          onClick: () => handleTabChange("all_"),
          leftHand: false,
        },
        canManagePermissions
          ? {
              id: "permission",
              label: "Permissions",
              className: "tab-trapezium-cycles-corners",
              background: "var(--primary-60)",
              activeColor: "white",
              onClick: () => handleTabChange("permission"),
              leftHand: false,
            }
          : null,
        canModifyFirmAvatar
          ? {
              id: "avatar",
              label: "Avatar",
              className: "tab-trapezium-cycles-corners",
              background: "var(--primary-60)",
              activeColor: "white",
              onClick: () => handleTabChange("avatar"),
              leftHand: false,
            }
          : null,
      ].filter(Boolean),
    [canManagePermissions, canModifyFirmAvatar, handleTabChange]
  );
  const [addUserModal, setAddUserModal] = useState(false);
  const addTab = [
    {
      id: "addUser",
      label: "User",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",

      left: "0px",
      onClick: () => setAddUserModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold ic-15 d-flex align-items-center">
          <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
        </span>
      ),
    },
  ];

  const handleHintToggle = () => {
    setShowHints(!showHints);
  };

  useEffect(() => {
    if (data) {
      setPermissions(data);
    }
  }, [data]);

  const handleOpenModal = (user) => {
    if (!canResetPassword) return;
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleSavePassword = async (newPassword) => {
    if (!canResetPassword) throw new Error("Only the firm owner can reset user passwords.");
    const userId = selectedUser?.user?.id;
    if (!userId) throw new Error("Select a user from your firm.");
    const { data } = await api.post("/api/firmsetting-page/reset-user-password/", {
      user_id: userId,
      password: newPassword,
    });
    toast.success(data?.message || "Password reset successfully.");
  };
  // const handlePermissionChange = async (userId, permissionKey, value) => {
  //   const userIndex = permissions.findIndex((user) => user.user.id === userId);
  //   const user = permissions[userIndex];

  //   const previousValue = permissionKey === "is_active" ? user.user.is_active : user[permissionKey];

  //   let newValue;
  //   if (
  //     permissionKey === "accounting_permission" ||
  //     permissionKey === "accounting_admin_permission" ||
  //     permissionKey === "reset_password_permission" ||
  //     permissionKey === "isIntake" ||
  //     permissionKey === "is_active"
  //   ) {
  //     newValue = value ? "True" : "False";
  //   } else {
  //     newValue = value;
  //   }

  //   const updatedPermissions = [...permissions];

  //   if (permissionKey === "is_active") {
  //     updatedPermissions[userIndex] = {
  //       ...user,
  //       user: {
  //         ...user.user,
  //         is_active: newValue === "True" ? true : false,
  //       },
  //     };
  //   } else if (permissionKey === "isIntake") {
  //     updatedPermissions[userIndex] = {
  //       ...user,
  //       isIntake: newValue === "True" ? true : false,
  //     };
  //   } else {
  //     updatedPermissions[userIndex] = {
  //       ...user,
  //       [permissionKey]: newValue,
  //     };
  //   }
  //   setPermissions(updatedPermissions);

  //   try {
  //     const payload = {
  //       user_id: userId,
  //       is_accounting:
  //         permissionKey === "accounting_permission"
  //           ? newValue
  //           : user.accounting_permission === "True"
  //             ? "True"
  //             : "False",
  //       is_admin:
  //         permissionKey === "accounting_admin_permission"
  //           ? newValue
  //           : user.accounting_admin_permission === "True"
  //             ? "True"
  //             : "False",
  //       is_intake:
  //         permissionKey === "isIntake" ? newValue : user.isIntake === true ? "True" : "False",
  //       is_active:
  //         permissionKey === "is_active"
  //           ? newValue
  //           : user.user.is_active === true
  //             ? "True"
  //             : "False",
  //       reset_password:
  //         permissionKey === "reset_password_permission"
  //           ? newValue
  //           : user.reset_password_permission === "True"
  //             ? "True"
  //             : "False",
  //     };

  //     const response = await api_without_cancellation.post(
  //       "/api/firmsetting-page/update-useraccount-permissions/",
  //       payload
  //     );
  //     if (response.ok) {
  //       Toast({
  //         message: "Permissions updated successfully",
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error updating permissions:", error);

  //     const revertedPermissions = [...permissions];
  //     revertedPermissions[userIndex] = {
  //       ...user,
  //       [permissionKey]: previousValue,
  //     };
  //     setPermissions(revertedPermissions);
  //   }
  // };
  const [hoveredId, setHoveredId] = useState(null);
  const handleModalShow = (e, userData) => {
    e.preventDefault();
    if (canEditUsers) {
      setShowEditModal(true);
      setUserData(userData);
    }
  };
  return (
    <div className="tab-pane fade firm-settings-user-perms-fs active show">
      <div className="m-b-5 p-r-5">
        <input
          type="search"
          className="form-control rounded-0 height-25"
          aria-label="Search Users"
          placeholder="Search Users"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="d-flex">
        <SmartResponsiveTabsContainer
          HandlePage={activeTab}
          tabsData={mainTabs}
          viewTab={[]}
          addTab={addTab}
          photoTab={[]}
          viewType={""}
          right={10}
        />
        {/* <div className="w-100">
          <NewTabs
            currentTab={activeTab}
            tabsData={mainTabs}
            height={25}
            labelClass=" justify-content-start"
          />
        </div>
        <NewTabs
          currentTab={activeTab}
          tabsData={addTab}
          height={25}
          containerClass="ps-2 pe-2"
          labelClass=" justify-content-center"
        /> */}

        {userExists?.attorney_user && (
          <div className="">
            <nav className="notes-nav-tab pe-0">
              <div
                className="nav nav-tabs d-flex-lg-1  ps-2 justify-content-end"
                id="nav-tab-account-checks"
                role="tablist"
                style={{
                  overflow: "clip",
                }}
              >
                <a
                  className={`nav-item nav-link nav-responsive ${activeTab === "admin" ? "active" : ""} show rlmargin-z note-categories-spaces`}
                  id="custom-nav-all-tab"
                  href="#custom-nav-all"
                  role="tab"
                  aria-controls="custom-nav-all"
                  aria-selected="true"
                  onClick={() => handleTabChange("admin")}
                >
                  Admin
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
      <div className="tab-content">
        {!["permission", "avatar", "admin"].includes(activeTab) && (
          <table
            className={
              "table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table"
            }
            id="treatment-summary-table"
          >
            <thead>
              <tr id="tb-header">
                <th />
                {canEditUsers && !isStatusTab && <th className="" />}
                <th className="text-uppercase text-center">Home Office</th>
                {isCollapsed ? (
                  <>
                    <th className="td-autosize text-uppercase text-center">User</th>
                    <th />
                  </>
                ) : (
                  <>
                    <th className="text-uppercase text-center">Firm User</th>
                    <th className="text-uppercase text-center">Username</th>
                    <th className="text-uppercase text-center">email</th>
                  </>
                )}
                {userExists?.attorney_user && (
                  <>
                    {activeTab === "all_" && (
                      <>
                        <th className="text-uppercase" />{" "}
                        {/* <th
                          style={{
                            minWidth: "10px",
                            maxWidth: "10px",
                            width: "10px",
                          }}
                        /> */}
                      </>
                    )}

                    {/* <th className="text-uppercase">Accounting</th>
                    <th className="text-uppercase">Admin</th>
                    <th className="text-uppercase">Intake</th>
                    <th className="text-uppercase">Active</th>
                    <th className="text-uppercase">Reset Password</th> */}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 &&
                filteredUsers.map((userData, idx) => (
                  <tr
                    key={userData.user.id}
                    onClick={canEditRows ? (e) => handleModalShow(e, userData) : undefined}
                    tabIndex={canEditRows ? 0 : undefined}
                    onKeyDown={
                      canEditRows
                        ? (e) => {
                            if (
                              e.target === e.currentTarget &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              handleModalShow(e, userData);
                            }
                          }
                        : undefined
                    }
                    style={{
                      height: isCollapsed ? "auto" : "35px",
                      cursor: canEditRows ? "pointer" : "default",
                    }}
                  >
                    <td className="td-autosize text-center" style={{ color: "var(--primary-25)" }}>
                      {idx + 1}
                    </td>
                    {canEditUsers && !isStatusTab && (
                      <td className="td-autosize text-center">
                        <div className="d-flex p-l-5 align-items-center height-25 justify-content-center">
                          <Button
                            variant={hoveredId === userData.user.id ? "success" : "secondary"}
                            className="btn btn-secondary height-25 d-flex align-items-center justify-content-center btn-hover-green"
                            onClick={(e) => handleModalShow(e, userData)}
                            onMouseEnter={() => setHoveredId(userData.user.id)}
                            onMouseLeave={() => setHoveredId(null)}
                          >
                            Edit
                          </Button>
                        </div>
                      </td>
                    )}
                    <td
                      className="td-autosize text-center"
                      style={{
                        fontWeight: "600",
                      }}
                      id="padding-left-right"
                    >
                      {userData?.for_office?.nickname}
                    </td>
                    {isCollapsed ? (
                      <>
                        <td className="text-center" id="padding-left-right">
                          <div
                            className="d-flex flex-column align-items-start p-t-5 p-b-5"
                            style={{
                              gap: "0px",
                            }}
                          >
                            <UserChatBubble
                              first_name={userData?.user?.first_name}
                              last_name={userData?.user?.last_name}
                              profilePic={userData?.profile_pic_29p ?? ""}
                              // onClick={(e) => openChatModel(e, userData)}
                              hideIcon={true}
                              isActive={userOnlineStatus?.[userData?.user?.id]?.isOnline ?? false}
                              is_active={userData?.user?.is_active}
                            />
                            <span style={{ fontSize: "14px", fontWeight: "600" }}>
                              {userData?.user?.username}
                            </span>
                            <span style={{ fontSize: "13px", fontWeight: "600" }}>
                              {userData?.user?.email}
                            </span>
                          </div>
                        </td>
                        <td />
                      </>
                    ) : (
                      <>
                        <td className="text-center" style={{ width: "4%" }}>
                          <span className="d-flex align-items-center justify-content-center">
                            <UserChatBubble
                              first_name={userData?.user?.first_name}
                              last_name={userData?.user?.last_name}
                              profilePic={userData?.profile_pic_29p ?? ""}
                              // onClick={(e) => openChatModel(e, userData)}
                              hideIcon={true}
                              isActive={userOnlineStatus?.[userData?.user?.id]?.isOnline ?? false}
                              is_active={userData?.user?.is_active}
                            />
                          </span>
                        </td>
                        <td
                          className="td-autosize text-center"
                          id="padding-left-right"
                          style={{ fontSize: "14px", fontWeight: "600" }}
                        >
                          {userData?.user?.username}
                        </td>
                        <td
                          className="text-center"
                          id="padding-left-right"
                          style={{ fontSize: "13px", width: "5%", fontWeight: " 600" }}
                        >
                          {userData?.user?.email}
                        </td>
                      </>
                    )}
                    {userExists?.attorney_user && (
                      <>
                        {activeTab === "all_" && (
                          <>
                            <td className="td-autosize text-center">
                              <div className="m-r-5">
                                <button
                                  className="btn btn-secondary height-25 d-flex align-items-center btn-hover-green"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(userData);
                                  }}
                                >
                                  Reset Password
                                </button>
                              </div>
                            </td>
                            {/* <td
                              className="td-autosize"
                              style={{
                                minWidth: "10px",
                                width: "10px",
                                maxWidth: "10px",
                              }}
                            /> */}
                          </>
                        )}
                        {/* <td className="text-center">
                          <input
                            type="checkbox"
                            checked={userData?.accounting_permission === "True"}
                            onChange={(e) => {
                              handlePermissionChange(
                                userData.user.id,
                                "accounting_permission",
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={userData?.accounting_admin_permission === "True"}
                            onChange={(e) => {
                              handlePermissionChange(
                                userData.user.id,
                                "accounting_admin_permission",
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={userData?.isIntake === true}
                            onChange={(e) => {
                              handlePermissionChange(
                                userData.user.id,
                                "isIntake",
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={userData?.user?.is_active === true}
                            onChange={(e) => {
                              handlePermissionChange(
                                userData.user.id,
                                "is_active",
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={userData?.reset_password_permission === "True"}
                            onChange={(e) => {
                              handlePermissionChange(
                                userData.user.id,
                                "reset_password_permission",
                                e.target.checked
                              );
                            }}
                          />
                        </td> */}
                      </>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        {activeTab === "permission" && canManagePermissions && (
          <>
            <UserPermissions searchTerm={searchTerm} />
          </>
        )}

        {activeTab === "avatar" && canModifyFirmAvatar && (
          <>
            <UserAvatar searchTerm={searchTerm} />
          </>
        )}

        {canResetPassword && showModal && (
          <ResetPasswordModal
            show={showModal}
            handleClose={handleCloseModal}
            handleSave={handleSavePassword}
            userName={[selectedUser?.user?.first_name, selectedUser?.user?.last_name]
              .filter(Boolean)
              .join(" ")}
          />
        )}
        {showEditModal && (
          <EditUserModal
            show={showEditModal}
            handleClose={() => setShowEditModal(false)}
            userData={userData}
            refetch={refetch}
          />
        )}
        {addUserModal && (
          <AddUserModalFirmSettings
            show={addUserModal}
            handleClose={() => setAddUserModal(false)}
            refetch={refetch}
          />
        )}
        {showHints && <HintModal show={showHints} handleClose={handleHintToggle} points={points} />}
        {chatModal && (
          <ChatSendingPopup
            data={chatData}
            show={chatModal}
            handleClose={() => setChatModal(false)}
            first_name={chatData?.user?.first_name}
            last_name={chatData?.user?.last_name}
            userId={chatData?.user?.id}
            profilePic={chatData?.profile_pic_29p || avatarImage}
            is_active={chatData?.user?.is_active}
          />
        )}
      </div>
    </div>
  );
};

export default UserAccounts;
